import { Injectable } from '@angular/core';
import { INotesComponent, INotesSection } from '@modeso/types__aumico-lib-annualreport-be';
import { ComponentType } from '@modeso/types__aumico-lib-notes-be';
import { EMPTY_ARRAY_LENGTH } from '../../../../../../aumico-lib-core-fe/src/lib';

@Injectable({ providedIn: 'root' })
export class AttachmentReferencesHelper {
    constructor() { }

    public getBalanceIncomeAttachmentReferences(
        notesSectionsWithNumbering: INotesSection[],
        language: string,
    ): Map<string, string> {
        const referencingMap: Map<string, string> = new Map<string, string>();
        let sectionNumber = 0;
        for (const notesSectionWithNumbering of notesSectionsWithNumbering) {
            const isAccordionHidden =
                notesSectionWithNumbering.data.length > 0 &&
                notesSectionWithNumbering.data.every((c) => c.isHidden === true);

            if (isAccordionHidden) {
                continue;
            }

            sectionNumber++;
            // For References on Notes Section Titles
            if (notesSectionWithNumbering.referenceFlis != null && notesSectionWithNumbering.referenceFlis.length !== EMPTY_ARRAY_LENGTH) {
                for (const referenceFli of notesSectionWithNumbering.referenceFlis) {
                    if (referenceFli === undefined || referenceFli === null) {
                        continue;
                    }
                    if (referencingMap.has(referenceFli)) {
                        const existingReferencingValue = referencingMap.get(referenceFli);
                        referencingMap.set(
                            referenceFli,
                            existingReferencingValue + `,` + sectionNumber,
                        );
                    } else {
                        referencingMap.set(
                            referenceFli,
                            sectionNumber.toString(),
                        );
                    }
                }
            }

            for (const componentWithNumbering of notesSectionWithNumbering.data) {
                // For References On Component Titles
                if (
                    componentWithNumbering.addNumbering &&
                    componentWithNumbering.referenceFlis != null &&
                    componentWithNumbering.referenceFlis.length !== EMPTY_ARRAY_LENGTH
                ) {
                    const componentNumber = this.getTitleNumbering(componentWithNumbering, language);
                    if (componentNumber === undefined || componentNumber === null) {
                        continue;
                    }
                    for (const referenceFli of componentWithNumbering.referenceFlis) {
                        if (referenceFli === undefined || referenceFli === null || componentNumber === '') {
                            continue;
                        }
                        if (referencingMap.has(referenceFli)) {
                            const existingReferencingValue = referencingMap.get(referenceFli);
                            referencingMap.set(referenceFli, existingReferencingValue + `, ${componentNumber}`);
                        } else {
                            referencingMap.set(referenceFli, componentNumber);
                        }
                    }
                }

                // For References on Fli and Movement Schedule Tables
                if (
                    componentWithNumbering.componentName === ComponentType.FINANCIAL_LINE_ITEM &&
                    componentWithNumbering.data.flis != null
                ) {
                    for (const referenceFli of componentWithNumbering.data.flis) {
                        if (referenceFli === undefined || referenceFli === null) {
                            continue;
                        }
                        if (referenceFli.addNumbering === false) {
                            continue;
                        }
                        const title = referenceFli.caption;
                        const tableNumber = this.extractNumber(title);


                        if (referencingMap.has(referenceFli.id) && tableNumber !== '') {
                            const existingReferencingValue = referencingMap.get(referenceFli.id);
                            referencingMap.set(referenceFli.id, existingReferencingValue + `, ${tableNumber}`);
                        } else if (tableNumber !== '') {
                            referencingMap.set(referenceFli.id, tableNumber);
                        }
                    }
                }

                if (
                    componentWithNumbering.componentName === ComponentType.MOVEMENT_SCHEDULE &&
                    componentWithNumbering.data.flis != null
                ) {
                    for (const referenceFli of componentWithNumbering.data.flis) {
                        if (referenceFli === undefined || referenceFli === null) {
                            continue;
                        }
                        if (referenceFli.addNumbering === false) {
                            continue;
                        }

                        const title = this.getLocalizedText(referenceFli.caption, language);
                        const tableNumber = this.extractNumber(title);

                        if (referencingMap.has(referenceFli.id) && tableNumber !== '') {
                            const existingReferencingValue = referencingMap.get(referenceFli.id);
                            referencingMap.set(referenceFli.id, existingReferencingValue + `, ${tableNumber}`);
                        } else if (tableNumber !== '') {
                            referencingMap.set(referenceFli.id, tableNumber);
                        }
                    }
                }

                if (
                    !componentWithNumbering.data.addNumbering ||
                    componentWithNumbering.data.referenceFlis == null ||
                    componentWithNumbering.data.referenceFlis.length === EMPTY_ARRAY_LENGTH
                ) {
                    continue;
                }

                // For References on other Tables
                if (componentWithNumbering.componentName === ComponentType.EQUITY) {
                    const title = this.getLocalizedText(componentWithNumbering.data.options?.textTitle, language);
                    const tableNumber = this.extractNumber(title);


                    for (const referenceFli of componentWithNumbering.data.referenceFlis) {
                        if (referencingMap.has(referenceFli) && tableNumber !== '') {
                            const existingReferencingValue = referencingMap.get(referenceFli);
                            referencingMap.set(referenceFli, existingReferencingValue + `, ${tableNumber}`);
                        } else if (tableNumber !== '') {
                            referencingMap.set(referenceFli, tableNumber);
                        }
                    }
                }

                if (
                    componentWithNumbering.componentName === ComponentType.FREE_TABLE_WITH_AMOUNTS ||
                    componentWithNumbering.componentName === ComponentType.SELECT ||
                    componentWithNumbering.componentName === ComponentType.SUPER_TABLE
                ) {
                    const title = this.getLocalizedText(componentWithNumbering.data.options?.title, language);
                    const tableNumber = this.extractNumber(title);


                    for (const referenceFli of componentWithNumbering.data.referenceFlis) {
                        if (referenceFli === undefined || referenceFli === null) {
                            continue;
                        }
                        if (referencingMap.has(referenceFli) && tableNumber !== '') {
                            const existingReferencingValue = referencingMap.get(referenceFli);
                            referencingMap.set(referenceFli, existingReferencingValue + `, ${tableNumber}`);
                        } else if (tableNumber !== '') {
                            referencingMap.set(referenceFli, tableNumber);
                        }
                    }
                }
            }
        }
        return referencingMap;
    }

    private getTitleNumbering(component: INotesComponent, language: string): string {
        if (component.componentTitle) {
            return this.extractNumber(this.getLocalizedText(component.componentTitle, language));
        }

        // For the users that previously saved titles in these properties as component titles

        if (component.componentName === ComponentType.RICH_TEXT && component.data.title != null) {
            return this.extractNumber(this.getLocalizedText(component.data.title, language));
        }

        return undefined;
    }

    private getLocalizedText(
        text: string | Record<string, string> | undefined | null,
        language: string,
    ): string | undefined {
        if (text == null) {
            return undefined;
        }

        if (typeof text === 'string') {
            return text;
        }

        return text[language] || text['de-ch'] || Object.values(text).find((value) => value != null && value !== '');
    }

    private extractNumber(text: string | undefined): string {
        if (text == null) {
            return '';
        }
        const match = text.match(/^[\d.]+/);
        return match ? match[0] : '';
    }
}
